/**
 * Performance Monitor JavaScript functions
 */

// Check if Performance API is available
function isPerformanceApiAvailable() {
    return typeof performance !== "undefined" && 
           typeof performance.memory !== "undefined" &&
           typeof performance.timing !== "undefined";
}

// Get browser performance metrics matching C# BrowserPerformanceMetrics class
function getBrowserPerformanceMetrics() {
    if (!isPerformanceApiAvailable()) {
        console.warn("Performance API not available");
        return null;
    }

    try {
        const timing = performance.timing;
        const navigationStart = timing.navigationStart;
        
        const metrics = {
            // Memory metrics (Chrome only)
            usedJSHeapSize: performance.memory?.usedJSHeapSize || 0,
            totalJSHeapSize: performance.memory?.totalJSHeapSize || 0,
            jsHeapSizeLimit: performance.memory?.jsHeapSizeLimit || 0,
            
            // Navigation timing
            pageLoadTime: timing.loadEventEnd ? timing.loadEventEnd - navigationStart : 0,
            dnsTime: timing.domainLookupEnd ? timing.domainLookupEnd - timing.domainLookupStart : 0,
            connectionTime: timing.connectEnd ? timing.connectEnd - timing.connectStart : 0,
            ttfb: timing.responseStart ? timing.responseStart - timing.requestStart : 0,
            
            // Resource and DOM metrics
            resourceCount: performance.getEntriesByType ? 
                performance.getEntriesByType("resource").length : 0,
            domElements: document.getElementsByTagName("*").length,
            
            // Paint metrics
            firstPaint: 0,
            firstContentfulPaint: 0,

            // Additional timing
            timeOrigin: performance.timeOrigin || navigationStart,
            now: performance.now() || 0
        };

        // Add paint metrics if available
        if (performance.getEntriesByType) {
            const paintMetrics = performance.getEntriesByType("paint");
            metrics.firstPaint = paintMetrics.find(
                entry => entry.name === "first-paint"
            )?.startTime || 0;
            metrics.firstContentfulPaint = paintMetrics.find(
                entry => entry.name === "first-contentful-paint"
            )?.startTime || 0;
        }

        return metrics;
    } catch (error) {
        console.error("Error collecting performance metrics:", error);
        return null;
    }
}

// Format bytes to human readable format
function formatBytes(bytes) {
    if (bytes === 0 || !bytes) return "0 Bytes";
    try {
        const k = 1024;
        const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
    } catch (error) {
        console.error("Error formatting bytes:", error);
        return "0 Bytes";
    }
}

// Copy text to clipboard with Promise return
function copyToClipboard(text) {
    if (!text) {
        return Promise.resolve(false);
    }

    return new Promise((resolve) => {
        try {
            if (navigator.clipboard && window.isSecureContext) {
                navigator.clipboard.writeText(text)
                    .then(() => resolve(true))
                    .catch((error) => {
                        console.error("Clipboard API error:", error);
                        resolve(false);
                    });
            } else {
                // Fallback for non-secure contexts
                const textArea = document.createElement("textarea");
                textArea.value = text;
                textArea.style.position = "fixed";
                textArea.style.opacity = "0";
                document.body.appendChild(textArea);
                textArea.select();
                
                const success = document.execCommand("copy");
                document.body.removeChild(textArea);
                resolve(success);
            }
        } catch (error) {
            console.error("Copy to clipboard error:", error);
            resolve(false);
        }
    });
}

/**
 * Download file helper for exporting metrics
 * @param {string} filename - The name of the file to download
 * @param {string} base64Data - The base64 encoded file content
 */
function downloadFile(filename, base64Data) {
    try {
        // Create blob from base64 data
        const byteCharacters = atob(base64Data);
        const byteNumbers = new Array(byteCharacters.length);
        
        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], {
            type: getContentType(filename)
        });

        // Create download link
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = filename;
        
        // Trigger download
        document.body.appendChild(link);
        link.click();
        
        // Cleanup
        document.body.removeChild(link);
        window.URL.revokeObjectURL(link.href);
    } catch (error) {
        console.error("Error downloading file:", error);
    }
}

/**
 * Get content type based on file extension
 * @param {string} filename
 * @returns {string} content type
 */
function getContentType(filename) {
    const ext = filename.split(".").pop().toLowerCase();
    const contentTypes = {
        'csv': "text/csv",
        'json': "application/json",
        'xlsx': "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        'xls': "application/vnd.ms-excel"
    };
    return contentTypes[ext] || "application/octet-stream";
}

window.PerformanceMonitor = {
    isPerformanceApiAvailable: isPerformanceApiAvailable,
    getBrowserPerformanceMetrics: getBrowserPerformanceMetrics,
    formatBytes: formatBytes,
    copyToClipboard: copyToClipboard,
    downloadFile: downloadFile,
    getContenType: getContentType
}
